<c-container [fluid]="true" class="py-3">
  <div class="d-flex align-items-center gap-2 mb-3">
    <button cButton color="secondary" variant="outline" size="sm" (click)="back()">
      ‹ {{ 'viacarte_admin.back' | translate }}
    </button>
    <h2 class="mb-0">{{ 'viacarte_admin.detail_title' | translate }}</h2>
  </div>

  <div *ngIf="loading()" class="text-center py-5"><c-spinner></c-spinner></div>
  <div *ngIf="error()" class="alert alert-danger">{{ error() }}</div>

  <ng-container *ngIf="!loading() && detail() as d">
    <!-- Aktions-Feedback -->
    <div *ngIf="actionMessage() as m" class="alert" [ngClass]="'alert-' + m.type">{{ m.text }}</div>

    <c-row class="g-3">
      <!-- Stammdaten + Aktionen -->
      <c-col [lg]="5">
        <c-card class="mb-3">
          <c-card-header class="d-flex align-items-center">
            <strong class="me-auto">{{ d.first_name }} {{ d.last_name }}</strong>
            <c-badge [color]="d.status_badge">{{ d.status || '—' }}</c-badge>
          </c-card-header>
          <c-card-body>
            <table cTable [small]="true" class="mb-0">
              <tbody>
                <tr><td class="text-medium-emphasis">User ID</td><td>{{ d.id }}</td></tr>
                <tr><td class="text-medium-emphasis">{{ 'viacarte_admin.email' | translate }}</td><td>{{ d.email }}</td></tr>
                <tr><td class="text-medium-emphasis">{{ 'viacarte_admin.brand' | translate }}</td><td><c-badge color="info">{{ d.brand }}</c-badge> (domain {{ d.domain_id }})</td></tr>
                <tr><td class="text-medium-emphasis">{{ 'viacarte_admin.customer_id' | translate }}</td><td><code class="small">{{ d.customer_id || '—' }}</code></td></tr>
                <tr><td class="text-medium-emphasis">Pre-Reg ID</td><td><code class="small">{{ d.preregistration_id || '—' }}</code></td></tr>
                <tr><td class="text-medium-emphasis align-top">{{ 'viacarte_admin.accounts' | translate }}</td>
                  <td>
                    <div *ngFor="let s of systemList()" class="d-flex align-items-center gap-2">
                      <c-badge [color]="s.accountId ? 'info' : 'secondary'">{{ ('viacarte_admin.system_' + s.system) | translate }}</c-badge>
                      <code class="small">{{ s.accountId || '—' }}</code>
                    </div>
                  </td>
                </tr>
                <tr><td class="text-medium-emphasis">{{ 'viacarte_admin.registered_at' | translate }}</td><td>{{ formatDate(d.registered_at) }}</td></tr>
                <tr *ngIf="d.workflow_url"><td class="text-medium-emphasis">Workflow</td><td><a [href]="d.workflow_url" target="_blank" rel="noopener">Link</a></td></tr>
              </tbody>
            </table>
          </c-card-body>
        </c-card>

        <c-card>
          <c-card-header><strong>{{ 'viacarte_admin.actions' | translate }}</strong></c-card-header>
          <c-card-body class="d-flex flex-column gap-2">
            <!-- System wählen (Account + Karten sind je System getrennt). Steuert
                 Provisionierung, Kontostand und Transfer. -->
            <div>
              <label class="form-label small mb-1">{{ 'viacarte_admin.system' | translate }}</label>
              <select cSelect [ngModel]="selectedSystem()" (ngModelChange)="selectedSystem.set($event)">
                <option *ngFor="let s of systemList()" [value]="s.system">
                  {{ ('viacarte_admin.system_' + s.system) | translate }}{{ s.accountId ? '' : ' · ' + ('viacarte_admin.no_account_short' | translate) }}
                </option>
              </select>
            </div>
            <button cButton color="info" variant="outline" [disabled]="busy() === 'refresh'" (click)="refresh()">
              <c-spinner *ngIf="busy() === 'refresh'" size="sm" class="me-1"></c-spinner>
              {{ 'viacarte_admin.action_refresh' | translate }}
            </button>
            <button cButton color="success" [disabled]="!canProvision() || busy() === 'provision'" (click)="provision()">
              <c-spinner *ngIf="busy() === 'provision'" size="sm" class="me-1"></c-spinner>
              {{ 'viacarte_admin.action_provision' | translate }} · {{ ('viacarte_admin.system_' + selectedSystem()) | translate }}
            </button>
            <small *ngIf="!canProvision()" class="text-medium-emphasis">
              {{ 'viacarte_admin.provision_hint' | translate }}
            </small>
          </c-card-body>
        </c-card>

        <c-card class="mt-3">
          <c-card-header><strong>{{ 'viacarte_admin.transfer_title' | translate }}</strong>
            <c-badge color="info" class="ms-2">{{ ('viacarte_admin.system_' + selectedSystem()) | translate }}</c-badge>
          </c-card-header>
          <c-card-body>
            <div *ngIf="!hasAccount()" class="text-medium-emphasis mb-0">{{ 'viacarte_admin.no_account' | translate }}</div>
            <ng-container *ngIf="hasAccount()">
              <c-input-group class="mb-2">
                <span cInputGroupText>{{ accountCurrency() || 'Betrag' }}</span>
                <input cFormControl type="number" min="0" step="0.01"
                       [placeholder]="'viacarte_admin.amount' | translate"
                       [ngModel]="fundAmount()"
                       (ngModelChange)="fundAmount.set($event)" />
              </c-input-group>
              <div class="d-flex gap-2">
                <button cButton color="success" class="flex-fill"
                        [disabled]="busy() === 'fund'" (click)="fund()">
                  <c-spinner *ngIf="busy() === 'fund'" size="sm" class="me-1"></c-spinner>
                  {{ 'viacarte_admin.fund' | translate }}
                </button>
                <button cButton color="warning" variant="outline" class="flex-fill"
                        [disabled]="busy() === 'withdraw'" (click)="withdraw()">
                  <c-spinner *ngIf="busy() === 'withdraw'" size="sm" class="me-1"></c-spinner>
                  {{ 'viacarte_admin.withdraw' | translate }}
                </button>
              </div>
            </ng-container>
          </c-card-body>
        </c-card>
      </c-col>

      <!-- Kontostand + Karten + Pre-Registration -->
      <c-col [lg]="7">
        <c-card class="mb-3">
          <c-card-header><strong>{{ 'viacarte_admin.account_balance' | translate }}</strong>
            <c-badge color="info" class="ms-2">{{ ('viacarte_admin.system_' + selectedSystem()) | translate }}</c-badge>
          </c-card-header>
          <c-card-body>
            <div *ngIf="!selectedAccountId()" class="text-medium-emphasis mb-0">{{ 'viacarte_admin.no_account' | translate }}</div>
            <div *ngIf="accountBalanceError() as be" class="alert alert-warning mb-0">{{ be }}</div>
            <c-row *ngIf="selectedAccountId() && !accountBalanceError() && accountBalance() as bal" class="g-2 text-center">
              <c-col [sm]="3" [xs]="6">
                <div class="text-medium-emphasis small">{{ 'viacarte_admin.bal_available' | translate }}</div>
                <div class="fs-5 fw-semibold">{{ formatAmount(bal.availableBalanceAmount, bal.balanceCurrency) }}</div>
              </c-col>
              <c-col [sm]="3" [xs]="6">
                <div class="text-medium-emphasis small">{{ 'viacarte_admin.bal_actual' | translate }}</div>
                <div class="fs-6">{{ formatAmount(bal.actualBalanceAmount, bal.balanceCurrency) }}</div>
              </c-col>
              <c-col [sm]="3" [xs]="6">
                <div class="text-medium-emphasis small">{{ 'viacarte_admin.bal_pending' | translate }}</div>
                <div class="fs-6">{{ formatAmount(bal.pendingBalanceAmount, bal.balanceCurrency) }}</div>
              </c-col>
              <c-col [sm]="3" [xs]="6">
                <div class="text-medium-emphasis small">{{ 'viacarte_admin.bal_blocked' | translate }}</div>
                <div class="fs-6">{{ formatAmount(bal.blockedBalanceAmount, bal.balanceCurrency) }}</div>
              </c-col>
            </c-row>
          </c-card-body>
        </c-card>

        <c-card class="mb-3">
          <c-card-header><strong>{{ 'viacarte_admin.cards' | translate }}</strong></c-card-header>
          <c-card-body>
            <div *ngIf="cardsError() as ce" class="alert alert-warning mb-0">{{ ce }}</div>
            <div *ngIf="!cardsError() && cards().length === 0" class="text-medium-emphasis">
              {{ 'viacarte_admin.no_cards' | translate }}
            </div>
            <table *ngIf="cards().length" cTable [responsive]="true" [small]="true" class="align-middle mb-0">
              <thead>
                <tr>
                  <th>{{ 'viacarte_admin.card_type' | translate }}</th>
                  <th>{{ 'viacarte_admin.card_number' | translate }}</th>
                  <th>{{ 'viacarte_admin.card_status' | translate }}</th>
                  <th class="text-end">{{ 'viacarte_admin.bal_available' | translate }}</th>
                  <th class="text-end">{{ 'viacarte_admin.actions' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let c of cards()">
                  <tr>
                    <td><c-badge [color]="cardTypeBadge(c.cardTypeLabel)">{{ c.cardTypeLabel || c.cardType }}</c-badge></td>
                    <td>
                      <code class="small">•••• {{ c.lastFourDigit || (c.maskedCardNumber || c.id) }}</code>
                      <div *ngIf="c.expiry" class="text-medium-emphasis small">exp {{ c.expiry }}</div>
                      <div *ngIf="c.cardToken" class="text-medium-emphasis small">Token {{ c.cardToken }}</div>
                    </td>
                    <td>
                      <c-badge [color]="cardStatusBadge(c)">{{ cardStatusLabel(c) }}</c-badge>
                    </td>
                    <td class="text-end text-nowrap">{{ cardAvailable(c) }}</td>
                    <td class="text-end text-nowrap">
                      <button cButton color="info" variant="outline" size="sm" class="me-1"
                              [disabled]="busy() === 'secure:' + c.id" (click)="revealCard(c)">
                        <c-spinner *ngIf="busy() === 'secure:' + c.id" size="sm" class="me-1"></c-spinner>
                        {{ (secureInfo()[c.id] ? 'viacarte_admin.hide_card_data' : 'viacarte_admin.show_card_data') | translate }}
                      </button>
                      <button *ngIf="c.statusNetwork !== 1000" cButton color="success" size="sm" class="me-1"
                              [disabled]="busy() === 'activate:' + c.id" (click)="activateCard(c)">
                        <c-spinner *ngIf="busy() === 'activate:' + c.id" size="sm" class="me-1"></c-spinner>
                        {{ 'viacarte_admin.activate' | translate }}
                      </button>
                      <button *ngIf="c.statusNetwork === 1000" cButton color="secondary" variant="outline" size="sm" class="me-1"
                              [disabled]="busy() === 'deactivate:' + c.id" (click)="deactivateCard(c)">
                        <c-spinner *ngIf="busy() === 'deactivate:' + c.id" size="sm" class="me-1"></c-spinner>
                        {{ 'viacarte_admin.deactivate' | translate }}
                      </button>
                      <!-- ViaCarte liefert keinen verlässlichen Block-Status im Card-Objekt,
                           daher beide Aktionen anbieten statt anhand isBlocked zu raten. -->
                      <button cButton color="danger" variant="outline" size="sm" class="me-1"
                              [disabled]="busy() === 'block:' + c.id" (click)="blockCard(c)">
                        {{ 'viacarte_admin.block' | translate }}
                      </button>
                      <button cButton color="success" variant="outline" size="sm"
                              [disabled]="busy() === 'unblock:' + c.id" (click)="unblockCard(c)">
                        {{ 'viacarte_admin.unblock' | translate }}
                      </button>
                    </td>
                  </tr>
                  <tr *ngIf="secureInfo()[c.id] as si">
                    <td colspan="5" class="bg-body-tertiary">
                      <div *ngIf="si.error" class="text-danger small mb-0">{{ si.error }}</div>
                      <div *ngIf="!si.error" class="d-flex flex-wrap align-items-end gap-4">
                        <div>
                          <div class="text-medium-emphasis small">{{ 'viacarte_admin.pan' | translate }}</div>
                          <code class="fs-6">{{ si.cardNumber }}</code>
                        </div>
                        <div>
                          <div class="text-medium-emphasis small">{{ 'viacarte_admin.expiry' | translate }}</div>
                          <code class="fs-6">{{ si.expiryDate }}</code>
                        </div>
                        <div>
                          <div class="text-medium-emphasis small">CVV</div>
                          <code class="fs-6">{{ si.cvv }}</code>
                        </div>
                        <div>
                          <div class="text-medium-emphasis small">{{ 'viacarte_admin.cardholder' | translate }}</div>
                          <span>{{ si.cardHolderName }}</span>
                        </div>
                        <button cButton color="secondary" variant="outline" size="sm" (click)="hideCard(c.id)">
                          {{ 'viacarte_admin.hide_card_data' | translate }}
                        </button>
                      </div>
                    </td>
                  </tr>
                </ng-container>
              </tbody>
            </table>

            <!-- Karte -> Karte (z.B. physisch -> virtuell) -->
            <div *ngIf="cards().length > 1" class="border-top pt-3 mt-3">
              <div class="small text-medium-emphasis mb-2">{{ 'viacarte_admin.card_transfer_title' | translate }}</div>
              <div class="d-flex flex-wrap gap-2 align-items-center">
                <select cSelect class="w-auto" [ngModel]="cardFrom()" (ngModelChange)="cardFrom.set($event)">
                  <option value="">{{ 'viacarte_admin.from_card' | translate }}</option>
                  <option *ngFor="let c of cards()" [value]="c.id">
                    {{ c.cardTypeLabel || c.cardType }} · {{ cardAvailable(c) }}
                  </option>
                </select>
                <span aria-hidden="true">→</span>
                <select cSelect class="w-auto" [ngModel]="cardTo()" (ngModelChange)="cardTo.set($event)">
                  <option value="">{{ 'viacarte_admin.to_card' | translate }}</option>
                  <option *ngFor="let c of cards()" [value]="c.id">{{ c.cardTypeLabel || c.cardType }}</option>
                </select>
                <input cFormControl type="number" min="0" step="0.01" class="w-auto" style="max-width: 140px;"
                       [placeholder]="'viacarte_admin.amount' | translate"
                       [ngModel]="cardTransferAmount()" (ngModelChange)="cardTransferAmount.set($event)" />
                <button cButton color="info" [disabled]="busy() === 'card-transfer'" (click)="cardTransfer()">
                  <c-spinner *ngIf="busy() === 'card-transfer'" size="sm" class="me-1"></c-spinner>
                  {{ 'viacarte_admin.transfer' | translate }}
                </button>
              </div>
            </div>

            <!-- Sandbox: ausgehende Test-Transaktion simulieren -->
            <div *ngIf="cards().length" class="border-top pt-3 mt-3">
              <div class="small text-medium-emphasis mb-2">{{ 'viacarte_admin.simulate_title' | translate }}</div>
              <div class="d-flex flex-wrap gap-2 align-items-center">
                <select cSelect class="w-auto" [ngModel]="cardFrom()" (ngModelChange)="cardFrom.set($event)">
                  <option value="">{{ 'viacarte_admin.from_card' | translate }}</option>
                  <option *ngFor="let c of cards()" [value]="c.id">{{ c.cardTypeLabel || c.cardType }}</option>
                </select>
                <input cFormControl type="number" min="0" step="0.01" class="w-auto" style="max-width: 120px;"
                       [placeholder]="'viacarte_admin.amount' | translate"
                       [ngModel]="simAmount()" (ngModelChange)="simAmount.set($event)" />
                <input cFormControl class="w-auto" style="max-width: 180px;"
                       [placeholder]="'viacarte_admin.merchant' | translate"
                       [ngModel]="simMerchant()" (ngModelChange)="simMerchant.set($event)" />
                <button cButton color="warning" variant="outline"
                        [disabled]="!cardFrom() || busy() === 'sim:' + cardFrom()"
                        (click)="simulate(cardFrom())">
                  <c-spinner *ngIf="busy() === 'sim:' + cardFrom()" size="sm" class="me-1"></c-spinner>
                  {{ 'viacarte_admin.simulate_button' | translate }}
                </button>
                <label class="d-flex align-items-center gap-1 small mb-0">
                  <input type="checkbox" class="form-check-input mt-0"
                         [ngModel]="simPost()" (ngModelChange)="simPost.set($event)" />
                  {{ 'viacarte_admin.simulate_post' | translate }}
                </label>
              </div>
            </div>
          </c-card-body>
        </c-card>

        <c-card class="mb-3">
          <c-card-header><strong>{{ 'viacarte_admin.transactions' | translate }}</strong></c-card-header>
          <c-card-body>
            <div *ngIf="transactions().length === 0" class="text-medium-emphasis mb-0">
              {{ 'viacarte_admin.no_transactions' | translate }}
            </div>
            <table *ngIf="transactions().length" cTable [responsive]="true" [small]="true" class="align-middle mb-0">
              <thead>
                <tr>
                  <th>{{ 'viacarte_admin.tx_date' | translate }}</th>
                  <th>{{ 'viacarte_admin.tx_description' | translate }}</th>
                  <th>{{ 'viacarte_admin.tx_source' | translate }}</th>
                  <th class="text-end">{{ 'viacarte_admin.tx_amount' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let t of pagedTransactions()">
                  <td class="small text-nowrap">{{ formatDate(t.date) }}</td>
                  <td>{{ t.description || '—' }}</td>
                  <td class="text-nowrap">
                    <span *ngIf="t.kind === 'card'">
                      <c-badge [color]="t.card_type_label === 'physical' ? 'success' : 'primary'">
                        {{ ('mycards.type_' + (t.card_type_label || 'card')) | translate }}
                      </c-badge>
                      <code class="small ms-1" *ngIf="t.card_last4">••••{{ t.card_last4 }}</code>
                    </span>
                    <c-badge *ngIf="t.kind === 'account'" color="secondary">{{ 'viacarte_admin.tx_account' | translate }}</c-badge>
                    <c-badge *ngIf="t.pending" color="warning" class="ms-1">{{ 'viacarte_admin.tx_pending' | translate }}</c-badge>
                    <button *ngIf="t.pending && t.internal_transaction_id" cButton color="success" variant="outline" size="sm" class="ms-2"
                            [disabled]="busy() === 'post:' + t.internal_transaction_id" (click)="postTransaction(t)">
                      <c-spinner *ngIf="busy() === 'post:' + t.internal_transaction_id" size="sm" class="me-1"></c-spinner>
                      {{ 'viacarte_admin.tx_confirm' | translate }}
                    </button>
                  </td>
                  <td class="text-end text-nowrap">{{ formatAmount(t.amount, t.currency || accountCurrency()) }}</td>
                </tr>
              </tbody>
            </table>

            <div *ngIf="transactions().length" class="d-flex flex-wrap align-items-center gap-2 mt-3">
              <div class="d-flex align-items-center gap-1">
                <span class="text-medium-emphasis small">{{ 'viacarte_admin.per_page' | translate }}</span>
                <select cSelect class="w-auto" [ngModel]="txPageSize()" (ngModelChange)="setTxPageSize($event)">
                  <option *ngFor="let s of txPageSizes" [ngValue]="s">{{ s }}</option>
                </select>
              </div>
              <div class="ms-auto d-flex align-items-center gap-2" *ngIf="txTotalPages() > 1">
                <button cButton color="secondary" variant="outline" size="sm"
                        [disabled]="txPage() <= 1" (click)="txGoTo(txPage() - 1)">‹</button>
                <span class="small text-medium-emphasis">{{ txPage() }} / {{ txTotalPages() }}</span>
                <button cButton color="secondary" variant="outline" size="sm"
                        [disabled]="txPage() >= txTotalPages()" (click)="txGoTo(txPage() + 1)">›</button>
              </div>
            </div>
          </c-card-body>
        </c-card>

        <c-card>
          <c-card-header><strong>{{ 'viacarte_admin.preregistration' | translate }}</strong></c-card-header>
          <c-card-body>
            <div *ngIf="preregError() as pe" class="alert alert-warning mb-0">{{ pe }}</div>
            <table *ngIf="!preregError()" cTable [small]="true" class="mb-0">
              <tbody>
                <tr *ngFor="let e of preregEntries()">
                  <td class="text-medium-emphasis text-nowrap">{{ e.key }}</td>
                  <td style="word-break: break-all;">{{ e.value }}</td>
                </tr>
                <tr *ngIf="preregEntries().length === 0">
                  <td class="text-medium-emphasis">{{ 'viacarte_admin.no_data' | translate }}</td>
                </tr>
              </tbody>
            </table>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>
  </ng-container>
</c-container>
